import React, { useState, useEffect, useMemo, useRef } from "react";
import { examinationScheduleApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./ExaminationSchedule.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const COURSES = [
  "B.A.", "B.Sc.", "B.Com.", "B.B.A.", "B.C.A.", "B.Tech.", 
  "M.A.", "M.Sc.", "M.Com.", "M.B.A.", "M.C.A.", "M.Tech.", "Ph.D."
];

const SEMESTERS = ["Sem 1", "Sem 2", "Sem 3", "Sem 4", "Sem 5", "Sem 6", "Sem 7", "Sem 8"];

const ACADEMIC_YEARS = ["2023-24", "2024-25", "2025-26", "2026-27"];

const EXAM_TYPES = [
  "End-Term Exam", "Mid-Term Exam", "Supplementary Exam", 
  "Practical Exam", "Viva Voce"
];

const STATUS_META = {
  Upcoming:  { label: "Upcoming",  color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  Ongoing:   { label: "Ongoing",   color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  Completed: { label: "Completed", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  Postponed: { label: "Postponed", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
  Draft:     { label: "Draft",     color: "#94a3b8", bg: "#f8fafc", border: "#e2e8f0" },
};

const SESSIONS = ['Morning (9:00 AM)', 'Afternoon (2:00 PM)', 'Evening (5:00 PM)'];
const DURATIONS = ['1 Hour', '2 Hours', '3 Hours', '4 Hours'];
const MODES = ['Written', 'Online', 'Practical', 'Viva'];

const COURSE_COLORS = [
  ["#7c3aed", "#f5f3ff"], ["#0ea5e9", "#f0f9ff"], ["#10b981", "#ecfdf5"],
  ["#f59e0b", "#fffbeb"], ["#ec4899", "#fdf2f8"], ["#6366f1", "#eef2ff"],
  ["#14b8a6", "#f0fdfa"], ["#f97316", "#fff7ed"], ["#8b5cf6", "#f5f3ff"],
  ["#0891b2", "#ecfeff"], ["#d97706", "#fffbeb"], ["#059669", "#ecfdf5"],
  ["#dc2626", "#fef2f2"],
];

const getCourseColor = (course) => COURSE_COLORS[COURSES.indexOf(course) % COURSE_COLORS.length] || COURSE_COLORS[0];

const defaultForm = {
  _id: null,
  Course: "B.Sc.",
  Semester: "Sem 1",
  Academic_Year: "2025-26",
  Examination_Type: "End-Term Exam",
  Exam_Code: "",
  Start_Date: "",
  End_Date: "",
  Venue_Exam_Hall: "",
  Chief_Invigilator: "",
  Subject_Schedule: [],
  Status: "Draft",
  Remarks_Instructions: "",
};

const ExaminationSchedule = () => {
  const [exams, setExams]                   = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [activeTab, setActiveTab]           = useState("info");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterCourse, setFilterCourse]     = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [searchText, setSearchText]         = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchExams(); }, []);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const response = await apiClient(examinationScheduleApi);
      const result = await response.json();
      if (response.ok) { setExams(result.data || []); }
    } catch (error) {
      console.error("Error fetching exams:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => exams.filter((ex) => {
    const q = searchText.toLowerCase();
    const matchSearch =
      (ex.Course || "").toLowerCase().includes(q) ||
      (ex.Exam_Code || "").toLowerCase().includes(q) ||
      (ex.Examination_Type || "").toLowerCase().includes(q);
    return matchSearch &&
      (filterCourse === "all" || ex.Course === filterCourse) &&
      (filterStatus === "all" || ex.Status === filterStatus);
  }), [exams, searchText, filterCourse, filterStatus]);

  const stats = useMemo(() => ({
    total:     exams.length,
    upcoming:  exams.filter(e => e.Status === "Upcoming").length,
    ongoing:   exams.filter(e => e.Status === "Ongoing").length,
    completed: exams.filter(e => e.Status === "Completed").length,
    postponed: exams.filter(e => e.Status === "Postponed").length,
    draft:     exams.filter(e => e.Status === "Draft").length,
  }), [exams]);

  const hasFilters = filterCourse !== "all" || filterStatus !== "all" || !!searchText;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const genCode = (course, sem) => {
    const prefix = "EX";
    const courseShort = (course || "XX").replace(/[^A-Z]/g, "").slice(0, 2);
    const semNum = (sem || "Sem 1").replace(/\D/g, "").slice(-1) || "1";
    const year = new Date().getFullYear();
    const rand = Math.floor(100 + Math.random() * 900);
    return `${prefix}-${courseShort}-S${semNum}-${year}-${rand}`;
  };

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 210, mH = 260, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (top + mH > window.innerHeight - 12) top = rect.top - mH - gap;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const openAddModal = () => {
    setFormData({ ...defaultForm, Exam_Code: genCode("B.Sc.", "Sem 1") });
    setModalType("add");
    setActiveTab("info");
    setDropdownOpenId(null);
  };
  const openViewModal = (ex) => { setSelectedItem(ex); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (ex) => {
    setFormData({
      ...ex,
      Start_Date: ex.Start_Date ? ex.Start_Date.split('T')[0] : "",
      End_Date: ex.End_Date ? ex.End_Date.split('T')[0] : "",
      Subject_Schedule: (ex.Subject_Schedule || []).map(s => ({
        ...s,
        Exam_Date: s.Exam_Date ? s.Exam_Date.split('T')[0] : ""
      }))
    });
    setModalType("edit");
    setActiveTab("info");
    setDropdownOpenId(null);
  };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };
  const openConfirm = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const next = { ...p, [name]: value };
      if (name === "Course" || name === "Semester") {
        next.Exam_Code = genCode(next.Course, next.Semester);
      }
      return next;
    });
  };

  const addSubjectRow = () => {
    setFormData(p => ({
      ...p,
      Subject_Schedule: [
        ...p.Subject_Schedule,
        { Subject_Name: "", Subject_Code: "", Exam_Date: "", Session: SESSIONS[0], Duration: DURATIONS[2], Mode: MODES[0], Hall_Venue: "", Max_Marks: 100, Pass_Marks: 40 }
      ]
    }));
  };

  const updateSubjectRow = (idx, field, val) => {
    setFormData(p => {
      const next = [...p.Subject_Schedule];
      next[idx] = { ...next[idx], [field]: val };
      return { ...p, Subject_Schedule: next };
    });
  };

  const removeSubjectRow = (idx) => {
    setFormData(p => ({
      ...p,
      Subject_Schedule: p.Subject_Schedule.filter((_, i) => i !== idx)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      if (modalType === "add") delete payload._id;
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? examinationScheduleApi : `${examinationScheduleApi}/${formData._id}`;
      const response = await apiClient(url, {
        method,
        body: JSON.stringify(payload),
      });
      if (response.ok) { fetchExams(); closeModal(); }
    } catch (error) {
      console.error("Error saving exam schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const ex = exams.find(e => e._id === id);
      await apiClient(`${examinationScheduleApi}/${id}`, {
        method: "PUT",
        body: JSON.stringify({ ...ex, Status: newStatus })
      });
      fetchExams();
      setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient(`${examinationScheduleApi}/${id}`, { method: "DELETE" });
      fetchExams();
      setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error deleting exam schedule:", error);
    }
  };

  const clearAllFilters = () => { setFilterCourse("all"); setFilterStatus("all"); setSearchText(""); };

  return (
    <div className="es-page">
      {/* ── Header ── */}
      <div className="es-header">
        <div className="es-header-left">
          <div className="es-header-icon">📝</div>
          <div>
            <div className="es-breadcrumb">
              <span>Examination</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
              <span className="es-bc-active">Examination Schedule</span>
            </div>
            <h1>Examination Schedule</h1>
            <p>Manage, declare and monitor end-term, mid-term and supplementary examination schedules.</p>
          </div>
        </div>
        <button type="button" className="es-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Create Schedule
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="es-stats">
        {[
          { key: "all",       label: "Total Schedules", icon: "📚", val: stats.total,     cls: "total" },
          { key: "Upcoming",  label: "Upcoming",        icon: "📅", val: stats.upcoming,  cls: "upcoming" },
          { key: "Ongoing",   label: "Ongoing",         icon: "⚡", val: stats.ongoing,   cls: "ongoing" },
          { key: "Completed", label: "Completed",       icon: "✅", val: stats.completed, cls: "completed" },
          { key: "Postponed", label: "Postponed",       icon: "⏳", val: stats.postponed, cls: "postponed" },
          { key: "Draft",     label: "Draft",           icon: "📝", val: stats.draft,     cls: "draft" },
        ].map((s) => (
          <div key={s.key} className={`es-stat-card ${s.cls} ${filterStatus === s.key ? "active-filter" : ""}`} onClick={() => setFilterStatus(s.key)}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content">
              <span>{s.label}</span>
              <strong>{s.val}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className="es-toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" placeholder="Search by code, course or exam type..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          {searchText && <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>}
        </div>
        <div className="toolbar-filters">
          <div className="toolbar-filter-group"><label>Course</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} className={`toolbar-select ${filterCourse !== "all" ? "active-select" : ""}`}>
              <option value="all">All Courses</option>{COURSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="toolbar-filter-group"><label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`toolbar-select ${filterStatus !== "all" ? "active-select" : ""}`}>
              <option value="all">All Statuses</option>{Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {hasFilters && <button type="button" className="clear-filters-btn" onClick={clearAllFilters}><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>Clear</button>}
          <span className="result-count"><strong>{filtered.length}</strong> of <strong>{exams.length}</strong></span>
        </div>
      </div>

      {/* ── Filter Pills ── */}
      {hasFilters && (
        <div className="active-filter-pills">
          {searchText && <span className="filter-pill">"{searchText}" <button type="button" onClick={() => setSearchText("")}>×</button></span>}
          {filterCourse !== "all" && <span className="filter-pill">Course: {filterCourse} <button type="button" onClick={() => setFilterCourse("all")}>×</button></span>}
          {filterStatus !== "all" && <span className="filter-pill">Status: {STATUS_META[filterStatus]?.label} <button type="button" onClick={() => setFilterStatus("all")}>×</button></span>}
          <button type="button" className="pill-clear-all" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <div className="loading-overlay">Loading examination schedules...</div>
      ) : filtered.length > 0 ? (
        <div className="es-grid">
          {filtered.map((ex) => {
            const st = STATUS_META[ex.Status] || STATUS_META.Draft;
            const [ac, ab] = getCourseColor(ex.Course);
            return (
              <div key={ex._id} className="es-card" onClick={() => openViewModal(ex)}>
                <div className="esc-head">
                  <div className="esc-icon-wrap" style={{ background: ab, color: ac }}>📋</div>
                  <div className="esc-meta">
                    <span className="esc-code">{ex.Exam_Code}</span>
                    <span className="esc-ay">{ex.Academic_Year}</span>
                  </div>
                  <div className="esc-right"><span className="status-badge" style={{ color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>{st.label}</span><button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, ex._id)}>⋮</button></div>
                </div>
                <div className="esc-course" style={{ color: ac }}>{ex.Course}</div>
                <div className="esc-sem-row"><span className="esc-sem">{ex.Semester}</span><span className="esc-type-badge">{ex.Examination_Type}</span></div>
                
                {ex.Subject_Schedule && ex.Subject_Schedule.length > 0 && (
                  <div className="esc-timeline">
                    {ex.Subject_Schedule.slice(0, 2).map((s, idx) => (
                      <div key={idx} className={`etl-item ${idx === 0 ? 'next' : ''}`}>
                        <div className="etl-date">
                          <span className="etl-day">{s.Exam_Date ? new Date(s.Exam_Date).toLocaleDateString('en-US', { weekday: 'short' }) : '??'}</span>
                          <span className="etl-num">{s.Exam_Date ? new Date(s.Exam_Date).getDate() : '—'}</span>
                        </div>
                        <div className="etl-dot" />
                        <div className="etl-info">
                          <span className="etl-subject">{s.Subject_Name}</span>
                          <span className="etl-mode">{s.Session}</span>
                        </div>
                      </div>
                    ))}
                    {ex.Subject_Schedule.length > 2 && <div className="etl-more">+{ex.Subject_Schedule.length - 2} more subjects...</div>}
                  </div>
                )}

                <div className="esc-footer">
                  <span>📅 {ex.Start_Date ? new Date(ex.Start_Date).toLocaleDateString() : 'TBD'} — {ex.End_Date ? new Date(ex.End_Date).toLocaleDateString() : 'TBD'}</span>
                  <span>{ex.Subject_Schedule?.length || 0} Subjects</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="es-empty">
          <div className="empty-icon">📅</div>
          <h3>No schedules found</h3>
          <p>{hasFilters ? "Try adjusting your filters or search term." : "No exam schedules have been created yet."}</p>
          {hasFilters && <button type="button" className="es-clear-btn" onClick={clearAllFilters}>Clear Filters</button>}
        </div>
      )}

      {/* ── Dropdown ── */}
      {dropdownOpenId && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}>
          <button type="button" onClick={() => openViewModal(exams.find(e => e._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" /><circle cx="12" cy="12" r="3" /></svg>View Details
          </button>
          <button type="button" onClick={() => openEditModal(exams.find(e => e._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>Edit Schedule
          </button>
          <div className="dropdown-divider" />
          {Object.entries(STATUS_META).map(([k, v]) => (
            <button key={k} onClick={() => openConfirm(dropdownOpenId, k.toLowerCase())}><span className="dd-dot" style={{ background: v.color }} />Set as {v.label}</button>
          ))}
          <div className="dropdown-divider" />
          <button type="button" className="dd-delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon ci-${confirmAction}`}>{confirmAction === "delete" ? "🗑" : "⚡"}</div>
            <h3>{confirmAction === "delete" ? "Delete Schedule?" : `Change Status to ${confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}?`}</h3>
            <p>{confirmAction === "delete" ? "This will permanently remove this exam schedule and all associated subject data." : `Are you sure you want to update the status of this schedule?`}</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button type="button" className={confirmAction === "delete" ? "btn btn-danger" : "btn btn-primary"} onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleStatusChange(confirmId, confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1))}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="es-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🗓</div>
                <div><h2>{modalType === "add" ? "Create Exam Schedule" : "Edit Exam Schedule"}</h2><span>{formData.Course} — {formData.Semester} ({formData.Academic_Year})</span></div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-tabs">
              <button type="button" className={`modal-tab ${activeTab === "info" ? "active" : "done"}`} onClick={() => setActiveTab("info")}><div className="tab-step">{activeTab === "info" ? "1" : "✓"}</div>Course & Info</button>
              <button type="button" className={`modal-tab ${activeTab === "subjects" ? "active" : formData.Subject_Schedule.length > 0 ? "done" : ""}`} onClick={() => setActiveTab("subjects")}><div className="tab-step">{activeTab === "subjects" ? "2" : "✓"}</div>Subject Schedule</button>
              <button type="button" className={`modal-tab ${activeTab === "status" ? "active" : ""}`} onClick={() => setActiveTab("status")}><div className="tab-step">3</div>Finalize & Status</button>
            </div>

            <form className="modal-form" onSubmit={handleSave}>
              {activeTab === "info" && (
                <div className="tab-content">
                  <div className="form-group full"><label>Course <span className="req">*</span></label>
                    <div className="course-picker-grid">
                      {COURSES.map(c => <button key={c} type="button" className={`cpc-card ${formData.Course === c ? "selected" : ""}`} onClick={() => handleChange({ target: { name: "Course", value: c } })}>{c}</button>)}
                    </div>
                  </div>
                  <div className="form-group full"><label>Semester <span className="req">*</span></label>
                    <div className="sem-picker">
                      {SEMESTERS.map(s => <button key={s} type="button" className={`sem-btn ${formData.Semester === s ? "selected" : ""}`} onClick={() => handleChange({ target: { name: "Semester", value: s } })}>{s}</button>)}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Academic Year</label><select name="Academic_Year" value={formData.Academic_Year} onChange={handleChange}>{ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
                    <div className="form-group"><label>Examination Type <span className="req">*</span></label><select name="Examination_Type" value={formData.Examination_Type} onChange={handleChange}>{EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Exam Code <span className="req">*</span></label><input type="text" name="Exam_Code" value={formData.Exam_Code} onChange={handleChange} readOnly /></div>
                    <div className="form-group"><label>Chief Invigilator</label><input type="text" name="Chief_Invigilator" value={formData.Chief_Invigilator} onChange={handleChange} placeholder="e.g. Dr. Rajesh Kumar" /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Start Date</label><input type="date" name="Start_Date" value={formData.Start_Date} onChange={handleChange} /></div>
                    <div className="form-group"><label>End Date</label><input type="date" name="End_Date" value={formData.End_Date} onChange={handleChange} /></div>
                  </div>
                  <div className="form-group full"><label>Primary Venue / Exam Hall</label><input type="text" name="Venue_Exam_Hall" value={formData.Venue_Exam_Hall} onChange={handleChange} placeholder="e.g. Block A — Main Exam Hall" /></div>
                </div>
              )}

              {activeTab === "subjects" && (
                <div className="tab-content subjects-tab">
                  <div className="subject-schedule-banner">
                    <div><strong>Define Subject Dates</strong><span>Add each subject, its exam date, session and venue.</span></div>
                    <div className="ssb-count"><strong>{formData.Subject_Schedule.length}</strong>Subjects</div>
                  </div>
                  <div className="subject-rows">
                    {formData.Subject_Schedule.map((s, idx) => (
                      <div key={idx} className="subject-row">
                        <div className="sr-num">{idx + 1}</div>
                        <div className="sr-fields">
                          <div className="sr-row-1">
                            <div className="sr-field"><label>Subject Name <span className="req">*</span></label><input type="text" value={s.Subject_Name} onChange={(e) => updateSubjectRow(idx, "Subject_Name", e.target.value)} placeholder="e.g. Data Structures" /></div>
                            <div className="sr-field"><label>Code <span className="req">*</span></label><input type="text" value={s.Subject_Code} onChange={(e) => updateSubjectRow(idx, "Subject_Code", e.target.value)} placeholder="BSC301" /></div>
                            <div className="sr-field"><label>Date <span className="req">*</span></label><input type="date" value={s.Exam_Date} onChange={(e) => updateSubjectRow(idx, "Exam_Date", e.target.value)} /></div>
                          </div>
                          <div className="sr-row-2">
                            <div className="sr-field"><label>Session</label><select value={s.Session} onChange={(e) => updateSubjectRow(idx, "Session", e.target.value)}>{SESSIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                            <div className="sr-field"><label>Duration</label><select value={s.Duration} onChange={(e) => updateSubjectRow(idx, "Duration", e.target.value)}>{DURATIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                            <div className="sr-field"><label>Mode</label><select value={s.Mode} onChange={(e) => updateSubjectRow(idx, "Mode", e.target.value)}>{MODES.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                            <div className="sr-field"><label>Hall</label><input type="text" value={s.Hall_Venue} onChange={(e) => updateSubjectRow(idx, "Hall_Venue", e.target.value)} placeholder="A-101" /></div>
                            <div className="sr-field"><label>Max</label><input type="number" value={s.Max_Marks} onChange={(e) => updateSubjectRow(idx, "Max_Marks", e.target.value)} /></div>
                            <div className="sr-field"><label>Pass</label><input type="number" value={s.Pass_Marks} onChange={(e) => updateSubjectRow(idx, "Pass_Marks", e.target.value)} /></div>
                          </div>
                        </div>
                        <button type="button" className="sr-delete" onClick={() => removeSubjectRow(idx)}>🗑</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-subject-btn" onClick={addSubjectRow}>+ Add Subject</button>
                </div>
              )}

              {activeTab === "status" && (
                <div className="tab-content">
                  <div className="form-group full"><label>Status</label>
                    <div className="status-grid">
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <label key={k} className={`status-card ${formData.Status === k ? "selected" : ""}`} style={formData.Status === k ? { borderColor: v.color, background: v.bg } : {}}>
                          <input type="radio" name="Status" value={k} checked={formData.Status === k} onChange={handleChange} />
                          <div><div className="sc-dot" style={{ background: v.color }} /><div><strong>{v.label}</strong><p>{k === "Draft" ? "Internal only" : k === "Upcoming" ? "Visible to students" : "Status update"}</p></div></div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="form-group full"><label>Remarks & Instructions</label><textarea name="Remarks_Instructions" value={formData.Remarks_Instructions} onChange={handleChange} placeholder="e.g. Reach 30 minutes before exam, bring ID cards..." /></div>
                </div>
              )}

              <div className="modal-footer">
                <div className="modal-footer-left"><button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button></div>
                <div className="modal-footer-right">
                  {activeTab !== "info" && <button type="button" className="btn btn-light" style={{ marginRight: 8 }} onClick={() => setActiveTab(activeTab === "status" ? "subjects" : "info")}>Previous</button>}
                  {activeTab !== "status" ? <button type="button" className="btn btn-primary" onClick={() => setActiveTab(activeTab === "info" ? "subjects" : "status")}>Next Step</button> : <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? "Saving..." : modalType === "add" ? "Create Schedule" : "Save Changes"}</button>}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="es-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="view-course-icon" style={{ background: getCourseColor(selectedItem.Course)[1], color: getCourseColor(selectedItem.Course)[0] }}>🗓</div>
                <div>
                  <div className="view-course-name">{selectedItem.Course} — {selectedItem.Semester}</div>
                  <div className="view-badges"><span className="view-code">{selectedItem.Exam_Code}</span><span className="view-sem">{selectedItem.Examination_Type}</span><span className="status-badge" style={{ color: STATUS_META[selectedItem.Status]?.color, background: STATUS_META[selectedItem.Status]?.bg }}>{selectedItem.Status}</span></div>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="view-modal-body">
              <div className="view-meta-grid">
                <div className="vmg-item"><div className="vmg-icon">🏫</div><div><span className="vmg-label">Year</span><span className="vmg-val">{selectedItem.Academic_Year}</span></div></div>
                <div className="vmg-item"><div className="vmg-icon">📍</div><div><span className="vmg-label">Venue</span><span className="vmg-val">{selectedItem.Venue_Exam_Hall || "TBD"}</span></div></div>
                <div className="vmg-item"><div className="vmg-icon">👨‍⚖️</div><div><span className="vmg-label">Invigilator</span><span className="vmg-val">{selectedItem.Chief_Invigilator || "TBD"}</span></div></div>
              </div>
              <div className="view-section-label">Subject Schedule</div>
              <div className="view-subject-table-wrap">
                <table className="view-subject-table">
                  <thead><tr><th className="col-num">#</th><th className="col-subject">Subject</th><th className="col-code">Code</th><th className="col-date">Date</th><th className="col-session">Session</th><th className="col-duration">Duration</th><th className="col-mode">Mode</th><th className="col-hall">Hall</th><th className="col-marks">Max</th></tr></thead>
                  <tbody>
                    {selectedItem.Subject_Schedule?.map((s, i) => (
                      <tr key={i}><td className="td-num">{i+1}</td><td className="td-subject">{s.Subject_Name}</td><td><span className="td-code-badge">{s.Subject_Code}</span></td><td>{s.Exam_Date ? new Date(s.Exam_Date).toLocaleDateString() : "TBD"}</td><td>{s.Session}</td><td>{s.Duration}</td><td>{s.Mode}</td><td>{s.Hall_Venue}</td><td style={{ textAlign:'center' }}>{s.Max_Marks}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedItem.Remarks_Instructions && (
                <>
                  <div className="view-section-label">Instructions</div>
                  <div className="view-about" style={{ fontSize: 13, lineHeight: 1.6 }}>{selectedItem.Remarks_Instructions}</div>
                </>
              )}
              <div className="view-footer-actions"><button type="button" className="btn btn-light" onClick={closeModal}>Close</button><button type="button" className="btn btn-primary" onClick={() => openEditModal(selectedItem)}>Edit Schedule</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExaminationSchedule;